<template>
  <BaseDialog
    :is-open="isRevealed"
    :disable-click-outside="true"
  >
    <div class="space-y-4">
      <div class="text-center text-lg font-medium">
        Signature OK?
      </div>

      <button
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-md border border-background-fg/20 bg-background px-4 py-2 text-base font-medium text-gray-700 shadow-sm"
        @click="onAccept"
      >
        Accept Signature
      </button>

      <button
        type="button"
        class="inline-flex w-full justify-center rounded-md bg-error px-4 py-2 text-base font-medium text-background shadow-sm"
        @click="onReject"
      >
        Reject
      </button>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import { IPayment } from "@/androidPlugins/IPayment"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { acceptSignature } from "@/services/transactions/acceptSignature"
import { retrieveTransactionReceipt } from "@/services/transactions/retrieveTransactionReceipt"
import { voidTransaction } from "@/services/transactions/voidTransaction"
import * as Sentry from "@sentry/vue"

const { reveal, isRevealed, confirm, cancel, onReveal } =
  useConfirmDialog<
    {
      transactionId: string
      transactionUti: string
      transactionAmount: number
    },
    any,
    any
  >()

const { tenant } = useTenant()
const { printingSupported } = useDevice()
const { startLoading, stopLoading } =
  usePageBlockingLoading()

defineExpose({
  reveal,
})

const transactionId = ref<string | null>(null)
const transactionUti = ref<string | null>(null)
const transactionAmount = ref<number | null>(null)

onReveal((payload) => {
  transactionId.value = payload.transactionId
  transactionUti.value = payload.transactionUti
  transactionAmount.value = payload.transactionAmount
})

const onAccept = async () => {
  if (!transactionId.value) {
    return
  }

  try {
    await acceptSignature(transactionId.value)
    confirm()
  } catch {
    toast.error("Something went wrong")
  }
}

const onReject = async () => {
  if (
    !transactionId.value ||
    !transactionUti.value ||
    transactionAmount.value === null
  ) {
    return
  }

  try {
    startLoading()
    const a920Response = await IPayment.makeDeviceVoid({
      uti: transactionUti.value,
      transactionAmount: transactionAmount.value,
    })

    if (a920Response.status !== "approved") {
      toast.error("Something went wrong")
      cancel()
      return
    }

    try {
      await voidTransaction(
        transactionId.value,
        a920Response.data
      )

      if (
        tenant.isAutomaticMerchantReceiptEnabled &&
        printingSupported.value
      ) {
        const { receipt } =
          await retrieveTransactionReceipt(
            transactionId.value,
            {
              isMerchant: true,
            }
          )

        await IPrinter.printStr({ str: receipt })
      }

      cancel()
    } catch {
      toast.error("Something went wrong")
      // Transaction has already been voided, so we should close this dialog, even if not synced with api
      cancel()
    }
  } catch (error) {
    Sentry.captureException(error)
    toast.error("Something went wrong")
    return
  } finally {
    stopLoading()
  }
}
</script>
